import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { VenueEventName } from '@/lib/shared-types';
import {
  onRealtimeReconnect,
  onSocketChange,
  releaseRooms,
  retainRooms,
  socketGeneration,
} from '@/lib/realtime';
import { useAppSelector } from '@/Redux/store';

/**
 * Subscribe to one domain event for as long as the component is mounted.
 *
 * **Rooms are not optional, they are defaulted.** `RealtimeService` emits to
 * `branch:`/`floor:`/`area:`/`session:`/`order:`/`station:` rooms and never to the
 * private `user:` room a socket joins from its token, so a subscription that
 * joins nothing receives nothing — silently, forever. That was 37 of the 40
 * subscriptions on this dashboard (F-02).
 *
 * Omitting `rooms` therefore means "the branch I am looking at", not "no
 * rooms": every event this dashboard cares about is emitted to its branch room
 * (see `realtime.service.ts` — `branch:` is in every single target list), so the
 * branch is both the minimum sufficient room and the widest one a staff socket
 * is reliably allowed to hold. A `tenant:` default was the alternative and is
 * wrong: `room-authorization.ts` grants a tenant room only to a TENANT- or
 * PLATFORM-scoped token, so a branch manager would simply be denied it and be
 * back to receiving nothing — the exact failure this default exists to remove.
 *
 * Pass `rooms` explicitly when a screen needs something narrower or wider than
 * one branch (a floor map watching `floor:`/`area:`, a queue spanning several
 * branches), **or when the screen's own branch filter can disagree with the
 * global one** — the kitchen board is the case that matters: it is keyed on a
 * station, and a station belongs to whatever branch it belongs to.
 *
 * When there is nothing to join — no branch selected and no rooms passed — the
 * subscription is inert, and in dev it says so rather than pretending to work.
 */
export function useRealtime<T = unknown>(
  event: VenueEventName,
  handler: (payload: T) => void,
  rooms?: string[],
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);

  const resolved = useMemo(
    () => (rooms?.length ? rooms : activeBranchId ? [`branch:${activeBranchId}`] : []),
    // `rooms` is usually a fresh array literal, so depend on its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rooms?.join('|'), activeBranchId],
  );

  const roomKey = resolved.join('|');

  /**
   * The socket's identity, not its state.
   *
   * An ordinary reconnect no longer changes it — `connectRealtime` re-arms the
   * same socket and fetches a fresh credential per handshake — but a sign-out,
   * and a human pressing Reconnect after realtime gave up, both replace the
   * object. Listeners registered on the old one would be orphaned on it, so this
   * is in the deps below: a new socket re-runs every subscription and re-joins
   * every room.
   */
  const generation = useSyncExternalStore(onSocketChange, socketGeneration, socketGeneration);

  useEffect(() => {
    if (import.meta.env.DEV && !roomKey) {
      console.warn(
        `[realtime] "${event}" is subscribed with no rooms — the server emits to ` +
          'branch/floor/area/session rooms, so this handler will never fire. ' +
          'Select a branch, or pass rooms to useRealtime().',
      );
    }

    const wanted = roomKey ? roomKey.split('|') : [];
    const socket = retainRooms(wanted);
    const listener = (payload: T) => handlerRef.current(payload);
    socket.on(event, listener);
    return () => {
      socket.off(event, listener);
      // The counterpart the leak was missing. Room membership is sticky and
      // replayed on every reconnect, so a screen that switches branch without
      // this watches both branches for the life of the tab.
      releaseRooms(wanted);
    };
  }, [event, roomKey, generation]);
}

/**
 * Re-read the server after realtime comes back.
 *
 * A board that patches its cache from events and never refetches drifts
 * silently across a reconnect: while the socket was down the venue kept
 * cooking, and every event fired in that window is gone — Socket.IO replays
 * nothing. The screen carries on looking live, showing a state that stopped
 * being true at the moment of the blip, which is worse than showing a spinner.
 *
 * Fires on every connect *after* the first, including the ones that follow a
 * socket being rebuilt after a refused handshake. The first connect is excluded
 * on purpose: the queries have just run.
 */
export function useRealtimeResync(onResync: () => void) {
  const handlerRef = useRef(onResync);
  handlerRef.current = onResync;

  useEffect(() => onRealtimeReconnect(() => handlerRef.current()), []);
}
